import { format } from "date-fns";
import { NotFoundImage } from "../utils/function";

export interface featuredShows {
  id: string;
  name: string;
  hostName: string;
  description?: string;
  thumbnail?: string;
  isActive: boolean;
  categories: Array<{
    id: string;
    name: string;
    isActive: boolean;
  }>;
  createdAt?: string;
  updatedAt: string;
};
interface FeaturedProps {
  featuredShows: featuredShows[];
  isLoading: boolean;
}

export default function FeaturedShows({ featuredShows, isLoading }: FeaturedProps) {
  if (isLoading) {
    return (
      <section className="py-24 pt-32 px-4 bg-linear-to-b from-[#042033] to-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-white text-3xl font-medium mb-4">Featured Shows</h2>
            <p className="text-white/60">Loading shows...</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden h-full">
                <div className="h-48 bg-white/10 animate-pulse" />
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-white/10 rounded w-3/4 animate-pulse" />
                  <div className="h-3 bg-white/10 rounded w-1/2 animate-pulse" />
                  <div className="h-3 bg-white/10 rounded w-full animate-pulse" />
                  <div className="h-3 bg-white/10 rounded w-5/6 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 pt-32 px-4 bg-linear-to-b from-[#042033] to-black relative">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-white text-3xl font-medium mb-4">Featured Shows</h2>
          <p className="text-white/60">Discover our most popular programs and podcasts</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredShows.map((show) => (
            <div key={show.id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden group hover:scale-[1.02] transition-transform duration-300">
              {/* Image Area */}
              <div className="h-48 relative overflow-hidden">
                <img
                  src={show.thumbnail || NotFoundImage}
                  alt={show.name}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = NotFoundImage;
                  }}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/80 to-transparent" />
              </div>

              {/* Content */}
              <div className="p-6 pt-0 relative -mt-8">
                {/* Categories */}
                <div className="flex gap-2 mb-4 flex-wrap">
                  {show.categories?.slice(0, 3).map((category) => (
                    <span
                      key={category.id}
                      className="text-xs font-medium text-cyan-100 bg-cyan-900/40 border border-cyan-500/30 px-2.5 py-1 rounded-md backdrop-blur-md"
                    >
                      {category.name}
                    </span>
                  ))}
                </div>

                <h3 className="text-white text-xl font-medium mb-2">{show.name}</h3>
                <p className="text-cyan-200 text-sm mb-4">with {show.hostName}</p>

                <p className="text-white/60 text-sm mb-6 leading-relaxed line-clamp-3">
                  {show.description || "No description available."}
                </p>

                <div className="pt-4 border-t border-white/10">
                  <p className="text-white/40 text-xs font-medium uppercase tracking-wider">
                    Updated {format(new Date(show.updatedAt), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}